import { Modal } from './Modal';
import { Button } from './Button';
import { AlertTriangle, Info, AlertCircle } from 'lucide-react';

type ConfirmVariant = 'danger' | 'warning' | 'info';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  loading?: boolean;
}

const icons: Record<ConfirmVariant, typeof AlertTriangle> = {
  danger: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const iconColors: Record<ConfirmVariant, string> = {
  danger: 'text-danger-600',
  warning: 'text-warning-600',
  info: 'text-info-600',
};

const confirmVariants: Record<ConfirmVariant, 'danger' | 'primary'> = {
  danger: 'danger',
  warning: 'primary',
  info: 'primary',
};

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
}: ConfirmDialogProps) {
  const Icon = icons[variant];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={confirmVariants[variant]} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex gap-4">
        <div className={`flex-shrink-0 ${iconColors[variant]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-surface-900">{title}</h3>
          <p className="mt-2 text-sm text-surface-600">{message}</p>
        </div>
      </div>
    </Modal>
  );
}
